/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/rodeos-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-rodeos-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/rodeos-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, primary_organization_id, default_handicap_organization_id
 * - Sortable fields: start_date, end_date, created_at, updated_at
 * - Required fields: id, buddy_groups_enabled, max_buddy_group_size, member_only_buddy_groups, duplicate_detection_mode, allow_non_member_buddy_group, allow_cash_payment, entry_close_time, timezone, draw_mode, scoring_state, allow_pay_later, is_finals
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/rodeos-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-rodeos-api-client.ts (API client)
 * - client/api/generated_schemas/rodeos.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-09-15T20:50:53.154Z
 * Source config: config/entities/rodeos-entity-config.ts
 * Public fields: 38/38 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RodeosClient, RodeosQueryOptions } from '../generated_clients/generated-rodeos-api-client.js';
import {
  RodeosInsert,
  RodeosUpdate,
  RodeosFieldConfig
} from '../../types/generated_schemas/generated-rodeos-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/rodeos_business_hooks/

// Query keys with enhanced caching strategy
export const rodeosKeys = {
  all: ['rodeos'] as const,
  lists: () => [...rodeosKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...rodeosKeys.lists(), { filters }] as const,
  details: () => [...rodeosKeys.all, 'detail'] as const,
  detail: (id: string) => [...rodeosKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...rodeosKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...rodeosKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...rodeosKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...rodeosKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useRodeosClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new RodeosClient();
};

/**
 * Hook to get all rodeos with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with rodeos data
 */
export const useRodeosList = (options?: RodeosQueryOptions) => {
  const client = useRodeosClient();
  
  return useQuery({
    queryKey: rodeosKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single rodeo by ID
 *
 * @param id - The rodeo ID
 * @returns React Query result with single rodeo data
 */
export const useRodeos = (id: string) => {
  const client = useRodeosClient();
  
  return useQuery({
    queryKey: rodeosKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/rodeos
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/rodeos_business_hooks/
 */

/**
 * Hook to create a new rodeo
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, buddy_groups_enabled, max_buddy_group_size, member_only_buddy_groups, duplicate_detection_mode, allow_non_member_buddy_group, allow_cash_payment, entry_close_time, timezone, draw_mode, scoring_state, allow_pay_later, is_finals
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating rodeo
 */
export const useCreateRodeos = () => {
  const client = useRodeosClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: RodeosInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rodeosKeys.all });
    },
  });
};

/**
 * Hook to update an existing rodeo
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating rodeo
 */
export const useUpdateRodeos = () => {
  const client = useRodeosClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RodeosUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: rodeosKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: rodeosKeys.lists() });
    },
  });
};

/**
 * Hook to delete a rodeo
 *
 * @returns React Query mutation for deleting rodeo
 */
export const useDeleteRodeos = () => {
  const client = useRodeosClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rodeosKeys.all });
    },
  });
};

/**
 * Hook to count rodeos with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, primary_organization_id, default_handicap_organization_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useRodeosCount = (filter?: Partial<Record<typeof RodeosFieldConfig.filterable[number], any>>) => {
  const client = useRodeosClient();
  
  return useQuery({
    queryKey: rodeosKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useRodeosFieldConfig = () => {
  return RodeosFieldConfig;
};